export const getPositionAttribute = (item) => item.position
export const MAX_SAFE_BIG_INT = Number.MAX_SAFE_INTEGER
export const MIN_SAFE_BIG_INT = -Number.MAX_SAFE_INTEGER
export const MAX_SAFE_INT = 2147483647
export const MIN_SAFE_INT = -2147483648

const defaultOptions = {
  max: MAX_SAFE_BIG_INT,
  min: MIN_SAFE_BIG_INT,
  getPosition: getPositionAttribute,
}

/*
 * Returns the midway point between two positions.
 */
export const between = (before, after) => before - (before - after) / 2

/*
 * Generates a new set of positions rebalanced
 * from min to max. Returns an equal number of
 * positions passed in.
 */
export const rebalance = (
  positions,
  max = defaultOptions.max,
  min = defaultOptions.min
) => {
  const gap = Math.floor(
    (max - min) / (positions.length + (positions.length % 2))
  )
  return new Array(positions.length)
    .fill(0)
    .map((pos, idx) => max - gap * (idx + 1))
}

export default class PositionedArrayManager {
  constructor(options) {
    this.options = Object.assign({}, defaultOptions, options)
  }

  /*
   * Moves a position to desired index.
   * Takes an array of items, each of which
   * must include a `position` attribute.
   * Rebalances all positions if necessary.
   * This is exclusively used for reordering
   * positions. Returns an array equal in
   * length to that of the array passed in.
   * The new array contains only the values
   * of any new positions, e.g.:
   * [null, null, 2353, null, null]
   * In this example, the only position that
   * needs to be updated is at index 2.
   */
  moveTo(array, index) {
    const positions = array.map(this.options.getPosition)
    const positionBeforeIndex =
      index === 0 ? this.options.min : positions[index - 1]
    const positionAfterIndex =
      index === positions.length ? this.options.max : positions[index]
    const newPosition = between(positionBeforeIndex, positionAfterIndex)

    if (
      positionBeforeIndex === positionAfterIndex ||
      newPosition === positionBeforeIndex ||
      newPosition === positionAfterIndex
    ) {
      positions.splice(index, 1, newPosition)
      return rebalance(positions, this.options.max, this.options.min)
    }
    // returns an array of null values and a position.
    // This position should be the only one that needs
    // to be updated server-side.
    return new Array(positions.length)
      .fill(null)
      .map((pos, idx) => (idx === index ? newPosition : pos))
  }

  // This will swap two positions at their respective indexes
  // if they exist in the array provided
  // and will return an array of positions
  swap(array = [], position1, position2) {
    const positions = array.map(this.options.getPosition)

    if (position1 === undefined || position2 === undefined) return positions

    const index1 = positions.indexOf(position1)
    const index2 = positions.indexOf(position2)

    if (index1 === -1 || index2 === -1) return positions

    // swap positions here
    positions[index1] = position2
    positions[index2] = position1

    return positions
  }

  // The shuffle method takes an array of objects, the from position, and two position,
  // the objects in the array need to have at least an position key and id key (or some other identifier)
  // the this.options.getPosition needs to map to create an array of objects of at least those two key attributes
  // getPosition: (item) -> {
  //    id: item.id,
  //    position: item.position
  // },
  // the method returns a shuffled array of objects in their new indexes
  // the objects contain the items identifier and position for you to then map your original array against
  shuffle(array = [], fromPosition, toPosition) {
    if (!array.length) return array

    const positions = array.map(this.options.getPosition)

    if (fromPosition === undefined || toPosition === undefined) return positions

    const fromIndex = positions.findIndex(
      (pos) => pos.position === fromPosition
    )
    const toIndex = positions.findIndex((pos) => pos.position === toPosition)

    if (fromIndex === -1 || toIndex === -1) return positions

    // shuffle positions here
    const fromPositionValue = positions.splice(fromIndex, 1)[0]
    positions.splice(toIndex, 0, fromPositionValue)

    return positions
  }

  /*
   * Adds a new position at the desired index.
   * Takes an array of items, each of which
   * must include a `position` attribute.
   * Rebalances all positions if necessary.
   * This is exclusively used for adding a new
   * positions, not reordering. Returns an array
   * equal in length to that of the array passed
   * in. The new array contains only the values
   * of any new positions, e.g.:
   * [null, null, 1, 9000, null]
   * In this example, the only positions that
   * need to be updated are at index 2 and 3.
   */

  insertAt(array, index) {
    const positions = array.map(this.options.getPosition)
    const hasMissingPositions = positions.some(
      (p) => p === null || typeof p === 'undefined'
    )

    if (hasMissingPositions) {
      let rebalanced = rebalance(positions, this.options.max, this.options.min)
      const positionBeforeIndex =
        index === 0 ? this.options.min : rebalanced[index - 1]
      const positionAfterIndex =
        index === rebalanced.length ? this.options.max : rebalanced[index]
      const newPosition = between(positionBeforeIndex, positionAfterIndex)

      rebalanced.splice(index, 0, newPosition)

      rebalanced = rebalance(rebalanced, this.options.max, this.options.min)
      return rebalanced.reverse()
    }

    const positionBeforeIndex =
      index === 0 ? this.options.min : positions[index - 1]
    const positionAfterIndex =
      index === positions.length ? this.options.max : positions[index]
    const newPosition = between(positionBeforeIndex, positionAfterIndex)

    if (
      positionBeforeIndex === positionAfterIndex ||
      newPosition === positionBeforeIndex ||
      newPosition === positionAfterIndex
    ) {
      positions.splice(index, 0, newPosition)
      return rebalance(positions, this.options.max, this.options.min)
    }
    // returns an array of null values and a position.
    // This position should be the only one that needs
    // to be updated server-side.
    return new Array(positions.length + 1)
      .fill(null)
      .map((pos, idx) => (idx === index ? newPosition : pos))
  }
}
